import { useState } from 'react';
import { useCart } from '../context/CartContext';

const categoryEmojis = {
  'dry-fruits': ['🪷', '🥜', '🌰', '🫘'],
  'jaggery': ['🟫', '✨'],
  'oils': ['🫒', '🥥', '🥜'],
  'spices': ['💛', '🌶️', '🫘', '🌿', '⚫', '🧂'],
  'grains': ['🌾'],
  'honey': ['🍯'],
};

const categoryColors = {
  'dry-fruits': '#E8D5B7',
  'jaggery': '#D4A574',
  'oils': '#C4A94D',
  'spices': '#C1784E',
  'grains': '#D4C89A',
  'honey': '#E8B931',
};

export default function ProductCard({ product, index = 0 }) {
  const [hovered, setHovered] = useState(false);
  const { addToCart } = useCart();

  const emojis = categoryEmojis[product.category] || ['🌿'];
  const emoji = emojis[index % emojis.length];
  const bgColor = categoryColors[product.category] || '#D4DCCA';

  const weightLabel = product.weight
    ? `${product.weight.value}${product.weight.unit}`
    : '';

  const discount = product.compareAtPrice
    ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
    : 0;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#FFFEF9',
        borderRadius: 16,
        overflow: 'hidden',
        transition: 'all 0.5s cubic-bezier(0.23,1,0.32,1)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered
          ? '0 20px 40px rgba(45,59,45,0.12), 0 4px 12px rgba(45,59,45,0.06)'
          : '0 2px 8px rgba(45,59,45,0.04)',
        border: '1px solid rgba(74,124,89,0.06)',
        animation: `fadeUp 0.5s ease-out ${index * 0.06}s both`,
      }}
    >
      {/* Visual */}
      <div style={{
        background: `linear-gradient(135deg, ${bgColor}30, ${bgColor}55)`,
        height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(circle at 30% 40%, ${bgColor}40, transparent 65%)`,
        }} />
        <span style={{
          fontSize: 60, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.08))',
          transition: 'transform 0.5s cubic-bezier(0.23,1,0.32,1)',
          transform: hovered ? 'scale(1.15) rotate(-4deg)' : 'scale(1)',
        }}>{emoji}</span>

        {/* Badges */}
        {weightLabel && (
          <span style={{
            position: 'absolute', top: 12, right: 12,
            background: '#2D5A3F', color: '#F5F0E8',
            fontSize: 11, padding: '3px 10px', borderRadius: 16,
            fontWeight: 600, letterSpacing: 0.3,
          }}>{weightLabel}</span>
        )}
        {discount > 0 && (
          <span style={{
            position: 'absolute', top: 12, left: 12,
            background: '#E85D3A', color: '#fff',
            fontSize: 11, padding: '3px 10px', borderRadius: 16,
            fontWeight: 700,
          }}>-{discount}%</span>
        )}
        <span style={{
          position: 'absolute', bottom: 12, left: 12,
          background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(4px)',
          color: '#4A7C59', fontSize: 9, padding: '3px 8px', borderRadius: 10,
          fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
        }}>Organic</span>
      </div>

      {/* Info */}
      <div style={{ padding: '18px 18px 20px' }}>
        <h3 style={{
          fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 700,
          color: '#2D3B2D', lineHeight: 1.3, marginBottom: 2,
        }}>{product.name}</h3>

        {product.nameHindi && (
          <p style={{
            fontSize: 12, color: '#7A8B6F', fontStyle: 'italic', marginBottom: 8,
          }}>{product.nameHindi}</p>
        )}

        <p style={{
          fontFamily: "'Cormorant Garamond', serif", fontSize: 14,
          color: '#5A6B5A', lineHeight: 1.6, marginBottom: 14,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>{product.shortDescription || product.description}</p>

        {/* Rating */}
        {product.ratingsAverage && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <span style={{ fontSize: 13 }}>⭐</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#2D3B2D' }}>
              {product.ratingsAverage}
            </span>
            <span style={{ fontSize: 11, color: '#7A8B6F' }}>
              ({product.ratingsQuantity || 0})
            </span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{
              fontFamily: "'Playfair Display', serif", fontSize: 22,
              fontWeight: 700, color: '#2D5A3F',
            }}>₹{product.price}</span>
            {product.compareAtPrice && (
              <span style={{
                fontSize: 13, color: '#A8B89C', textDecoration: 'line-through', marginLeft: 8,
              }}>₹{product.compareAtPrice}</span>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); addToCart(product); }}
            style={{
              background: hovered
                ? 'linear-gradient(135deg, #4A7C59, #2D5A3F)'
                : 'linear-gradient(135deg, #2D5A3F, #1E3F2B)',
              color: '#F5F0E8', border: 'none',
              padding: '9px 18px', borderRadius: 24,
              fontSize: 12, fontWeight: 600, transition: 'all 0.3s', letterSpacing: 0.3,
            }}
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}
